'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { evaluateVariantFlagClient } from '@/feature-flags/client/variant-evaluator';
import { useFeatureFlagProvider } from '@/feature-flags/client/provider';
import { FEATURE_FLAGS } from '@/feature-flags/consts/flags.const';
import type { UseSwitchFlexToWhatsappCitiesResult } from '@/feature-flags/types/switch-flex-to-whatsapp.types';
import { parseSwitchFlexToWhatsappCities } from '@/feature-flags/utils/switch-flex-to-whatsapp-parser';
import { logInfo, logWarning, logError } from '@/feature-flags/utils/logger';

export function useSwitchFlexToWhatsappCities(): UseSwitchFlexToWhatsappCitiesResult {
  const { isReady, error: providerError } = useFeatureFlagProvider();
  const [state, setState] = useState<UseSwitchFlexToWhatsappCitiesResult>({
    cities: [],
    loading: true,
    error: null,
    isFallback: true,
    variant: null,
    refetch: async () => {},
  });
  const hasEvaluatedRef = useRef(false);

  const evaluate = useCallback(async () => {
    const flagKey = FEATURE_FLAGS.OPERATIONAL_MEDPREV_WEB_APP_SWITCH_FLEX_TO_WHATSAPP;

    setState((current) => ({ ...current, loading: true, error: null }));

    try {
      logInfo('Evaluating switch flex to whatsapp flag', { flagKey });

      const result = await evaluateVariantFlagClient<string>(flagKey);

      if (!result.match) {
        logWarning('Switch flex to whatsapp no-match, using empty cities', {
          flagKey,
          variant: result.variant,
          reason: 'no-match-fallback',
        });
        setState((current) => ({
          ...current,
          cities: [],
          loading: false,
          isFallback: true,
          variant: result.variant,
        }));
        return;
      }

      const cities = parseSwitchFlexToWhatsappCities(result.attachment);

      logInfo('Switch flex to whatsapp cities loaded', {
        flagKey,
        variant: result.variant,
        cities,
        isFallback: result.isFallback,
      });

      setState((current) => ({
        ...current,
        cities,
        loading: false,
        error: null,
        isFallback: result.isFallback,
        variant: result.variant,
      }));
    } catch (error) {
      const evalError = error instanceof Error ? error : new Error(String(error));

      logError('Switch flex to whatsapp evaluation failed', error, { flagKey });

      setState((current) => ({
        ...current,
        cities: [],
        loading: false,
        error: evalError,
        isFallback: true,
        variant: null,
      }));
    }
  }, []);

  useEffect(() => {
    setState((current) => ({ ...current, refetch: evaluate }));
  }, [evaluate]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (providerError) {
      setState((current) => ({
        ...current,
        cities: [],
        loading: false,
        error: providerError,
        isFallback: true,
        variant: null,
      }));
      hasEvaluatedRef.current = true;
      return;
    }

    if (!hasEvaluatedRef.current) {
      hasEvaluatedRef.current = true;
      evaluate();
    }
  }, [isReady, providerError, evaluate]);

  return state;
}
